import * as React from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { ChevronRight } from "lucide-react";

import { FOCUS_RING, FOCUS_RING_INSET } from "../lib/focus";
import { HIT_AREA } from "../lib/target";
import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 Disclosure (redesign phase 5, #455): the one expand/collapse primitive.
 * Merges Venus `ui/accordion`, the unstyled shared `ui/collapsible` and the
 * request-cards `Disclosure` (break #97). Storybook only until implementation.
 *
 * Built on Radix Collapsible: the trigger is a button with `aria-expanded` and
 * `aria-controls`; Enter and Space toggle. No group mode (only one open): a
 * stack of disclosures opens independently, because closing the previous
 * section of long content makes the page jump (decided 05.10).
 *
 * The chevron leads and points right; open, it turns 90° (150ms; under reduced
 * motion it snaps). No height animation: the content appears at once.
 *
 *   md (default)  a 40px row, padding 16, title `text-label` `--ink`, hover
 *                 overlay. Made to sit flush in a Card (`<CardContent flush>`);
 *                 stacked rows get `--divider` lines from the caller
 *                 (`divide-y divide-divider`). The focus ring is inset, so a
 *                 card's `overflow-clip` can't cut it. Content lines up with
 *                 the title (40 = 16 + 16 + 8).
 *   sm            inline: 12px caption in `--ink-muted`, 12px chevron, hugs its
 *                 text ("Quellen" under an answer card).
 *
 * Both triggers carry `outline-hidden` (no browser ring), so their focus rings
 * set `outline-solid` explicitly: `outline-2` alone would keep style `none`.
 *
 * `icon` (optional) sits between the chevron and the title: 16 in md, 12 in sm,
 * `--ink-muted`, decorative. The content keeps its 40px inset, so it lines up
 * with the icon, and a stack with and without icons stays aligned.
 *
 * `meta` (a Badge, a count, a short text) sits after the title inside the
 * button, so it is part of the name; never put interactive content there.
 * `headingLevel` wraps the trigger in `h2`–`h6` where the title heads a section.
 */

export type DisclosureSize = "sm" | "md";

export interface DisclosureProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>, "title"> {
  title: React.ReactNode;
  /** Decorative icon before the title (e.g. Paperclip for an attachment). */
  icon?: IconComponent;
  meta?: React.ReactNode;
  size?: DisclosureSize;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Classes for the content region (e.g. a different padding for a flush table). */
  contentClassName?: string;
  /** Static states for stories (`data-hover`, `data-focus` on the trigger); apps never set them. */
  triggerProps?: React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Trigger> & Record<`data-${string}`, string>;
}

const TRIGGER: Record<DisclosureSize, string> = {
  md: cn(
    "flex min-h-10 w-full items-center gap-2 px-4 py-2 text-left font-sans text-label text-ink",
    "hover:bg-hover-overlay data-hover:bg-hover-overlay",
    FOCUS_RING_INSET,
  ),
  sm: cn(
    "inline-flex items-center gap-1 rounded-sm text-left font-sans text-caption text-ink-muted",
    "hover:text-ink data-hover:text-ink",
    FOCUS_RING,
    // 16 high at 12px text: the hit area grows to 24 (WCAG 2.5.8), the look stays.
    HIT_AREA,
  ),
};

const CONTENT: Record<DisclosureSize, string> = {
  md: "pb-3 pl-10 pr-4",
  sm: "mt-2",
};

const Disclosure = React.forwardRef<React.ElementRef<typeof CollapsiblePrimitive.Root>, DisclosureProps>(
  function Disclosure(
    { title, icon, meta, size = "md", headingLevel, contentClassName, triggerProps, className, children, ...props },
    ref,
  ) {
    const trigger = (
      <CollapsiblePrimitive.Trigger
        {...triggerProps}
        className={cn(
          "group cursor-pointer outline-hidden transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          TRIGGER[size],
          triggerProps?.className,
        )}
      >
        <Icon
          icon={ChevronRight}
          size={size === "sm" ? 12 : 16}
          className="text-ink-muted transition-transform duration-150 group-data-[state=open]:rotate-90 motion-reduce:transition-none"
        />
        {icon && <Icon icon={icon} size={size === "sm" ? 12 : 16} className="text-ink-muted" />}
        <span className="min-w-0">{title}</span>
        {meta != null && (
          <>
            {/* Word break for the accessible name; flex layout doesn't render it. */}{" "}
            <span className="flex shrink-0 items-center">{meta}</span>
          </>
        )}
      </CollapsiblePrimitive.Trigger>
    );

    const Heading = headingLevel ? (`h${headingLevel}` as const) : null;

    return (
      <CollapsiblePrimitive.Root
        ref={ref}
        className={cn(size === "md" ? "flex flex-col" : "flex flex-col items-start", className)}
        {...props}
      >
        {Heading ? <Heading className="flex">{trigger}</Heading> : trigger}
        <CollapsiblePrimitive.Content className={cn(CONTENT[size], size === "sm" && "self-stretch", contentClassName)}>
          {children}
        </CollapsiblePrimitive.Content>
      </CollapsiblePrimitive.Root>
    );
  },
);

export { Disclosure };
